import React from 'react';
import Modal from 'react-modal';
import { omit, words } from 'lodash';
import { assertions } from 'lib/assertions';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Dialog from 'components/Dialog';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import { IconName } from 'components/Icon';
import Input from 'components/Input';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import { patchParticipant } from 'coaching/actions/CoachingActions';
import Row from 'components/Row';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import { ParticipantRow } from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorPeopleListView';

type ValidAssertionNames =
  | 'email'
  | 'firstAndLastName'
  | 'url'
  | 'zipcode'
  | 'completePhoneNumber'
  | 'emailOrPlausibleNorthAmericanPhoneNumber'
  | 'uuid';
type ValidAssertions = Pick<typeof assertions, ValidAssertionNames>;

type Props = {
  participant: ParticipantRow;
  field: keyof Omit<ParticipantRow, 'subjects'>;
  fieldReadableName: string;
  iconName: IconName;
  assertionNames: (keyof ValidAssertions)[];

  // if editable is false, we still allow the user to add the field
  // when it doesn't exist - they just can't change it once they've
  // already input it
  editable: boolean;
};

type State = {
  editDialogIsOpen: boolean;
  userInput?: string;
};

export default class CoachingParticipantWhiteboardUrl extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      editDialogIsOpen: false,
      userInput: props.participant[props.field] ?? undefined,
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.participant[prevProps.field] !== this.props.participant[this.props.field]) {
      this.setState({ userInput: this.props.participant[this.props.field] ?? undefined });
    }
  }

  hideDialog = () => {
    this.setState({ editDialogIsOpen: false });
  };

  showDialog = () => {
    this.setState({ editDialogIsOpen: true });
  };

  handleSubmit = async () => {
    const { participant, field } = this.props;
    const { userInput } = this.state;
    if (!userInput) {
      console.error('CoachingParticipantWhiteboardUrl - Tried to submit without a url');
      return;
    }
    const { success } = await patchParticipant(participant.uuid, {
      // FIX: we currently expect whiteboardUrl to always be a string,
      // but it's currently `string | null` on the participant itself
      whiteboardUrl: participant.whiteboardUrl ?? '',
      purchaserUuid: participant.purchaserUuid,
      studentUuid: participant.studentUuid,
      studentName: participant.studentName,
      timezone: participant.timezone,
      distanceLearningPermalink: participant.distanceLearningPermalink,
      subjects: participant.subjects,
      // override the specified field with the user input
      [field]: userInput,
    });
    if (success) {
      this.hideDialog();
    }
  };

  getDataName = () => {
    return words(this.props.fieldReadableName.toLowerCase()).join('-');
  };

  renderLink = (text: string) => {
    return (
      <Link
        weight="semibold"
        onClick={this.showDialog}
        data-test={`edit-${this.getDataName()}-link`}>
        {text}
      </Link>
    );
  };

  render() {
    const {
      participant,
      field,
      fieldReadableName,
      iconName,
      assertionNames,
      editable,
    } = this.props;
    const { userInput } = this.state;
    const dataName = this.getDataName();

    const existingLinkComponent = (
      <MediaObject figure="right" data-test={`existing-${dataName}`}>
        <Text truncate>{participant[field]}</Text>
        {editable ? this.renderLink('Edit') : <span />}
      </MediaObject>
    );
    const noLinkComponent = this.renderLink(`Add ${fieldReadableName}`);
    return (
      <>
        <Modal data-test={`edit-${dataName}-dialog`} isOpen={this.state.editDialogIsOpen}>
          <Dialog size="small" onRequestHide={this.hideDialog}>
            <BoxBody>
              <Row size="small">
                <Heading serif>Edit {fieldReadableName}</Heading>
              </Row>
              <Row>
                <FormElement
                  data-test={`${dataName}-input`}
                  element={Input}
                  validate={e => assertionNames.forEach(name => assertions[name](e.target.value))}
                  onValidChange={e => this.setState({ userInput: e.target.value })}
                  onInvalidChange={() => this.setState({ userInput: undefined })}
                  label=""
                  elementProps={{
                    required: true,
                    name: dataName,
                    value: userInput,
                    placeholder: `Add ${fieldReadableName}`,
                  }}
                />
              </Row>
              <Button
                data-test={`edit-${dataName}-submit-button`}
                onClick={this.handleSubmit}
                full
                disabled={!userInput}>
                Save
              </Button>
            </BoxBody>
          </Dialog>
        </Modal>
        <TextWithIcon icon={iconName}>
          {participant[field] ? existingLinkComponent : noLinkComponent}
        </TextWithIcon>
      </>
    );
  }
}
